import * as React from 'react';
import VehicleSearch from 'components/Worksale/AutoInfoCatalogue/VehicleSearch';
import { useAutoCatalogueTypeChange, useInitialize, useManualVehicleSearch, useRegoStates, useRetrieveCatalogueEntries, useVehicleSearch } from 'api/Worksale/AutoInfoCatalogue/autoInfoCatalogue';
import { ViewList } from '@markinson/uicomponents-v2/SvgIcons';
import CatalogueEntries from './CatalogueEntries';
import { createStyles, Paper } from '@material-ui/core';
import Catalogue from './CataloguePartsGrid';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { withStyles } from '@material-ui/core/styles';
import { IAutoISearchVehicleResponse } from 'api/swaggerTypes';
import VehicleDetailsDialog from './VehicleDetailsDialog';
import { IAutoInfoCatalogueProps, transformStates } from './AutoInfoCatalogue.properties';
import { ICatalogueTypeItem } from 'components/Worksale/AutoInfoCatalogue/VehicleSearch/VehicleSearch.properties';
import { useVRContextSelector } from '../AutoInfoCatalogue/VehicleSearch/VehicleSearchContext';
import { ScrollPositionStateProvider } from './ScrollPositionContext';

const serviceUnavailable = 503;

const inlineStyles = createStyles({
  subDiv: {
    width: '100%',
    marginTop: '20px',
  },
  innerDiv: {
    display: 'flex',
  },
  lhsPaper: {
    width: '340px',
    padding: '10px',
    height: 'calc(100vh - 410px)',
    marginLeft: '10px'
  },
  rhsDiv: {
    width: 'calc(100% - 350px)',
    height: 'calc(100vh - 390px)',
    marginLeft: '10px',
    overflow: 'auto',
  },
});

const AutoInfoCatalogue = (props: IAutoInfoCatalogueProps): JSX.Element => {
  const { classes, vehicleDetailStates, allowPriceQuantityDialogAutoi, changeSelectedTab } = props;

  const autoIAPIErrorHandler = (err: any): void => {
    if (err?.res?.status === serviceUnavailable && changeSelectedTab) {
      changeSelectedTab('Lines');
    }
  };

  const cTypeId = useVRContextSelector<'CatalogueTypeId'>((state) => state.CatalogueTypeId);
  const vId = useVRContextSelector<'VehicleId'>((state) => state.VehicleId);

  const { mutateAsync: initializeAutoIMutation } = useInitialize(autoIAPIErrorHandler);
  const { mutateAsync: getRegoStatesMutation } = useRegoStates(autoIAPIErrorHandler);
  const { mutateAsync: vehicleSearchMutation } = useVehicleSearch(autoIAPIErrorHandler);
  const { mutateAsync: catalogueTypeChangeMutation } = useAutoCatalogueTypeChange(autoIAPIErrorHandler);
  const catalogueEntries = useRetrieveCatalogueEntries(cTypeId, vId, autoIAPIErrorHandler);
  const manualVehicleSearch = useManualVehicleSearch(autoIAPIErrorHandler);

  const [catalogueTypes, setCatalogueTypes] = React.useState<ICatalogueTypeItem[]>([]);
  const [defaultCatalogueType, setDefaultCatalogueType] = React.useState<number>(null);
  const { setActionBar } = React.useContext(ActionBarContext);

  React.useEffect(
    () => {
      initializeAutoI();
      setActionBarButtons();
    },
    []);

  React.useEffect(
    () => {
      setActionBarButtons();
    },
    [vId]
  );

  const setActionBarButtons = (): void => {
    setActionBar({
      leftIcons: [],
      centerIcons: [
        {
          label: 'Vehicle Details',
          Icon: ViewList,
          action: 'VehicleDetails',
          disabled: vId === -1 || vId === null || vId === undefined,
        },
      ],
      rightIcons: []
    });
  };

  const manualVehicleSearchHandler = React.useCallback(
    async (catalogueTypeId: number, data: any): Promise<IAutoISearchVehicleResponse> => {
      if (catalogueTypeId !== undefined && catalogueTypeId !== null && catalogueTypeId !== -1 && (data?.VehicleAttribute !== '' || undefined)) {
        const result = await manualVehicleSearch.mutateAsync(
          {
            ...data,
            VehicleAttribute: data?.VehicleAttribute ?? 'Make',
            CatalogueTypeId: catalogueTypeId,
          }
        );

        return result;
      }

      return null;
    },
    []
  );

  const initializeAutoI = async () => {
    const response = await initializeAutoIMutation({});
    if (response != null) {
      setDefaultCatalogueType(response?.AutoInfoConfiguration?.DefaultCatalogueTypeId);
      vehicleDetailStates?.setIncludeMoProImages(response?.AutoInfoConfiguration?.IncludeMoProImages);
      vehicleDetailStates?.setAllowPriceQuantityAutoi?.(response?.AutoInfoConfiguration?.AllowPriceQuantityAdjustment ?? false);
      setCatalogueTypes(response?.AutoInfoConfiguration?.CatalogueTypeDetails);
    }
  };

  const vehicleCatalogueTypeStates = async (CatalogueTypeId: number) => {
    const response = await getRegoStatesMutation({ CatalogueTypeId });
    const mapped = transformStates(response?.Data);

    return mapped ?? [];
  };

  const onCatalogueTypeChangeHandler = async (CatalogueTypeId: number) => {
    if (CatalogueTypeId) {
      catalogueTypeChangeMutation({ CatalogueType: CatalogueTypeId });
    }
  };

  const vehicleSearchHanlder = async (payload) => {
    const response = await vehicleSearchMutation(payload);

    return response;
  };

  return (
    <ScrollPositionStateProvider>
      <div className={classes.mainDiv}>
        <VehicleSearch
          defaultCatalogueTypeId={defaultCatalogueType}
          catalogueTypes={catalogueTypes}
          getCatalogueTypeStatesList={vehicleCatalogueTypeStates}
          searchVehicle={vehicleSearchHanlder}
          manualVehicleSearch={manualVehicleSearchHandler}
          onCatalogueTypeChage={onCatalogueTypeChangeHandler}
        />
        <div style={inlineStyles.subDiv}>
          <div style={inlineStyles.innerDiv}>
            <Paper style={inlineStyles.lhsPaper}>
              <CatalogueEntries catalogueEntriesData={catalogueEntries?.data} isLoading={catalogueEntries?.isFetching} />
            </Paper>
            <div style={inlineStyles.rhsDiv}>
              <Catalogue handleVehicleDetailsOnClick={vehicleDetailStates?.handleVehicleDetailsOnClick} allowPriceQuantityDialogAutoi={allowPriceQuantityDialogAutoi} errorHandler={autoIAPIErrorHandler} />
            </div>
          </div>
        </div>
        {
          vehicleDetailStates?.openVehicleDetailsDialog ? (
            <VehicleDetailsDialog
              open={vehicleDetailStates?.openVehicleDetailsDialog}
              onCancel={() => { vehicleDetailStates?.setOpenVehicleDetailsDialog(false); }}
              errorHandler={autoIAPIErrorHandler}
            />
          ) : null
        }
      </div>
    </ScrollPositionStateProvider>
  );
};

export default withStyles({}, { index: 1 })(React.forwardRef(AutoInfoCatalogue));
